import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useAuth } from '../context/AuthContext';
import {
  SERVICE_REQUESTS_QUERY, MY_SERVICE_REQUESTS_QUERY, CREATE_SERVICE_REQUEST_MUTATION,
  CLOSE_SERVICE_REQUEST_MUTATION, RESPOND_SERVICE_REQUEST_MUTATION, ACCEPT_SERVICE_RESPONSE_MUTATION,
} from '../graphql';
import { PERSONAS, PERSONA_GROUPS, personaLabel } from '../config/personas';

const money = (a, c) => a ? `${(a / 100).toFixed(0)} ${c || ''}`.trim() : 'Open budget';

function PersonaSelect({ value, onChange, allowBlank = true }) {
  return (
    <select className="input" value={value} onChange={e => onChange(e.target.value)}>
      {allowBlank && <option value="">Any category</option>}
      {Object.entries(PERSONA_GROUPS).map(([g, label]) => (
        <optgroup key={g} label={label}>
          {PERSONAS.filter(p => p.group === g).map(p => <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>)}
        </optgroup>
      ))}
    </select>
  );
}

// Respond to someone else's open need.
function OpenRequest({ req }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState('');
  const [quote, setQuote] = useState('');
  const [respond, { loading }] = useMutation(RESPOND_SERVICE_REQUEST_MUTATION, {
    refetchQueries: [{ query: SERVICE_REQUESTS_QUERY }],
    onCompleted: () => setOpen(false),
    onError: e => alert(e.message),
  });
  const mine = req.myResponse;
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
        <div className="avatar avatar-sm" style={{ background: req.posterAvatar || 'var(--primary)' }}>{req.posterName?.[0] || '?'}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700 }}>{req.title}</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            {req.posterName} · {req.city || 'anywhere'} {req.persona ? `· ${personaLabel(req.persona)}` : ''} · 💰 {money(req.budget, req.currency)}{req.neededBy ? ` · by ${req.neededBy}` : ''}
          </div>
          {req.description && <div style={{ fontSize: 'var(--font-sm)', marginBottom: 6 }}>{req.description}</div>}
          {mine ? (
            <div className="badge badge-success">✓ You responded{mine.quote ? ` — ${money(mine.quote, req.currency)}` : ''} · {mine.status}</div>
          ) : open ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
              <input className="input" style={{ flex: 1, minWidth: 160 }} placeholder="Your message" value={msg} onChange={e => setMsg(e.target.value)} />
              <input className="input" style={{ width: 130 }} type="number" min={0} placeholder="Quote (₹)" value={quote} onChange={e => setQuote(e.target.value)} />
              <button className="btn btn-primary btn-sm" disabled={loading} onClick={() => respond({ variables: { requestId: req.id, message: msg, quote: quote ? parseInt(quote) * 100 : 0 } })}>Send</button>
            </div>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>🙋 Respond</button>
          )}
        </div>
      </div>
    </div>
  );
}

// My posted need — see & accept responses.
function MyRequest({ req }) {
  const [accept] = useMutation(ACCEPT_SERVICE_RESPONSE_MUTATION, { refetchQueries: [{ query: MY_SERVICE_REQUESTS_QUERY }] });
  const [close] = useMutation(CLOSE_SERVICE_REQUEST_MUTATION, { refetchQueries: [{ query: MY_SERVICE_REQUESTS_QUERY }] });
  const statusColor = req.status === 'open' ? 'var(--success)' : req.status === 'fulfilled' ? 'var(--primary)' : 'var(--text-muted)';
  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700 }}>{req.title} <span className="badge" style={{ background: 'var(--surface2)', color: statusColor, textTransform: 'capitalize' }}>{req.status}</span></div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{req.city || 'anywhere'} {req.persona ? `· ${personaLabel(req.persona)}` : ''} · 💰 {money(req.budget, req.currency)} · {req.responseCount || 0} response{req.responseCount === 1 ? '' : 's'}</div>
        </div>
        {req.status === 'open' && <button className="btn btn-ghost btn-sm" onClick={() => close({ variables: { id: req.id } })}>Close</button>}
      </div>
      {(req.responses || []).length > 0 && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {req.responses.map(r => (
            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: 'var(--surface2)', borderRadius: 'var(--radius-sm)', flexWrap: 'wrap' }}>
              <div className="avatar avatar-sm" style={{ background: r.responderAvatar || 'var(--primary)' }}>{r.responderName?.[0] || '?'}</div>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontWeight: 600, fontSize: 'var(--font-sm)' }}>{r.responderName} {r.quote ? `· ${money(r.quote, req.currency)}` : ''}</div>
                {r.message && <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{r.message}</div>}
                {r.status === 'accepted' && r.responderUpi && <div style={{ fontSize: 12, color: 'var(--primary)' }}>📲 Pay: {r.responderUpi}</div>}
              </div>
              {r.status === 'accepted' ? <span className="badge badge-success">✓ Accepted</span>
                : r.status === 'declined' ? <span className="badge badge-dim">Declined</span>
                : req.status === 'open' ? <button className="btn btn-primary btn-sm" onClick={() => accept({ variables: { responseId: r.id } })}>Accept</button>
                : <span className="badge badge-dim">Pending</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ServiceRequests({ onAuthRequired }) {
  const { currentUser } = useAuth();
  const [tab, setTab] = useState('browse');
  const [persona, setPersona] = useState('');
  const [form, setForm] = useState({ title: '', description: '', persona: '', broadCategory: '', city: currentUser?.location?.city || '', budget: '', neededBy: '' });
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const { data: openData, loading: openLoading } = useQuery(SERVICE_REQUESTS_QUERY, { variables: { persona: persona || undefined }, skip: !currentUser, fetchPolicy: 'cache-and-network' });
  const { data: myData } = useQuery(MY_SERVICE_REQUESTS_QUERY, { skip: !currentUser, fetchPolicy: 'cache-and-network' });
  const [createRequest, { loading: creating }] = useMutation(CREATE_SERVICE_REQUEST_MUTATION, {
    refetchQueries: [{ query: MY_SERVICE_REQUESTS_QUERY }, { query: SERVICE_REQUESTS_QUERY }],
    onCompleted: () => { setForm({ title: '', description: '', persona: '', broadCategory: '', city: currentUser?.location?.city || '', budget: '', neededBy: '' }); setTab('mine'); },
    onError: e => alert(e.message),
  });

  if (!currentUser) return (
    <div className="page"><div className="container" style={{ paddingTop: 100, textAlign: 'center' }}>
      <p style={{ marginBottom: 16 }}>Sign in to post a need or respond to requests.</p>
      <button className="btn btn-primary" onClick={() => onAuthRequired?.('login')}>Sign in</button>
    </div></div>
  );

  const open = openData?.serviceRequests || [];
  const mine = myData?.myServiceRequests || [];

  return (
    <div className="page">
      <div className="container" style={{ padding: '32px 16px', maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 800, marginBottom: 4 }}>🙋 Post a Need</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-sm)' }}>Need a chef, tutor, coach, photographer or organiser? Post it — the right people respond with a quote.</p>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
          {[['browse', '🔎 Open needs'], ['post', '➕ Post a need'], ['mine', `📋 My needs${mine.length ? ` (${mine.length})` : ''}`]].map(([id, label]) => (
            <button key={id} className={`btn btn-sm ${tab === id ? 'btn-primary' : 'btn-ghost'}`} onClick={() => setTab(id)}>{label}</button>
          ))}
        </div>

        {tab === 'browse' && (
          <>
            <div style={{ marginBottom: 12, maxWidth: 320 }}><PersonaSelect value={persona} onChange={setPersona} /></div>
            {openLoading && open.length === 0 ? <div className="spinner" /> : open.length === 0 ? (
              <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No open needs right now. Check back soon!</div>
            ) : open.map(r => <OpenRequest key={r.id} req={r} />)}
          </>
        )}

        {tab === 'post' && (
          <div className="card">
            <div className="form-group"><label className="form-label">What do you need? *</label>
              <input className="input" placeholder="e.g. Birthday cake for Saturday" value={form.title} onChange={e => setF('title', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Details</label>
              <textarea className="input" rows={3} placeholder="Size, flavour, timing, location…" value={form.description} onChange={e => setF('description', e.target.value)} /></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">Category</label><PersonaSelect value={form.persona} onChange={v => setF('persona', v)} /></div>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">City</label><input className="input" value={form.city} onChange={e => setF('city', e.target.value)} placeholder="City" /></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">Budget ₹ (optional)</label><input className="input" type="number" min={0} value={form.budget} onChange={e => setF('budget', e.target.value)} placeholder="e.g. 1500" /></div>
              <div className="form-group" style={{ margin: 0 }}><label className="form-label">Needed by</label><input className="input" type="date" value={form.neededBy} onChange={e => setF('neededBy', e.target.value)} /></div>
            </div>
            <button className="btn btn-primary" style={{ width: '100%', marginTop: 16 }} disabled={creating || !form.title.trim()}
              onClick={() => createRequest({ variables: { title: form.title.trim(), description: form.description || null, persona: form.persona || null, city: form.city || null, budget: form.budget ? parseInt(form.budget) * 100 : 0, currency: 'INR', neededBy: form.neededBy || null } })}>
              {creating ? 'Posting…' : '📣 Post my need'}
            </button>
          </div>
        )}

        {tab === 'mine' && (
          mine.length === 0 ? <div className="card" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>You haven't posted any needs yet.</div>
            : mine.map(r => <MyRequest key={r.id} req={r} />)
        )}
      </div>
    </div>
  );
}
